import React, { useState } from 'react';
import { useAppStore } from '../store/app-store';
import { initializeCloudflareClient } from '../lib/cloudflare-client';

export function ConfigModal({ onClose }) {
  const { cloudflare, setCloudflareConfig } = useAppStore();

  const [accountId, setAccountId] = useState(cloudflare.accountId);
  const [apiToken, setApiToken] = useState(cloudflare.apiToken);
  const [workerUrl, setWorkerUrl] = useState(cloudflare.workerUrl || import.meta.env.VITE_WORKER_URL || '');
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const handleTest = async () => {
    if (!workerUrl) {
      setTestResult({ success: false, error: 'Please provide Worker URL. Deploy the worker first with: wrangler deploy' });
      return;
    }

    setTesting(true);
    setTestResult(null);

    try {
      const client = initializeCloudflareClient(accountId, apiToken, workerUrl);
      const result = await client.testConnection();
      setTestResult(result);
    } catch (error) {
      setTestResult({ success: false, error: error.message });
    } finally {
      setTesting(false);
    }
  };

  const handleSave = () => {
    if (!workerUrl) {
      alert('Please provide Worker URL. Deploy the worker first with: wrangler deploy');
      return;
    }

    setCloudflareConfig({ accountId, apiToken, workerUrl });
    initializeCloudflareClient(accountId, apiToken, workerUrl);
    onClose();
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content config-modal">
        <div className="modal-header">
          <h2>Cloudflare Configuration</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          <p className="info-text">
            Configure your Cloudflare account to use Workers AI.
            Get your credentials from the{' '}
            <a
              href="https://dash.cloudflare.com/"
              target="_blank"
              rel="noopener noreferrer"
            >
              Cloudflare Dashboard
            </a>
          </p>

          <div className="form-group">
            <label htmlFor="workerUrl">
              Worker URL
              <span className="required">*</span>
            </label>
            <input
              id="workerUrl"
              type="text"
              value={workerUrl}
              onChange={(e) => setWorkerUrl(e.target.value)}
              placeholder="https://cloudflare-dev-agent-worker.YOUR_SUBDOMAIN.workers.dev"
            />
            <span className="hint">
              Deploy the worker first with: wrangler deploy
            </span>
          </div>

          <div className="form-group">
            <label htmlFor="accountId">
              Account ID
              <span className="hint-text"> (optional - used by worker)</span>
            </label>
            <input
              id="accountId"
              type="text"
              value={accountId}
              onChange={(e) => setAccountId(e.target.value)}
              placeholder="Your Cloudflare Account ID"
            />
            <span className="hint">
              Found in Cloudflare Dashboard → Workers & Pages → Overview
            </span>
          </div>

          <div className="form-group">
            <label htmlFor="apiToken">
              API Token
              <span className="hint-text"> (optional - used by worker)</span>
            </label>
            <input
              id="apiToken"
              type="password"
              value={apiToken}
              onChange={(e) => setApiToken(e.target.value)}
              placeholder="Your Cloudflare API Token"
            />
            <span className="hint">
              Create a token with "Workers AI: Read" permission
            </span>
          </div>

          <div className="action-buttons">
            <button
              className="test-btn"
              onClick={handleTest}
              disabled={testing || !workerUrl}
            >
              {testing ? 'Testing...' : 'Test Connection'}
            </button>

            {testResult && (
              <div className={`test-result ${testResult.success ? 'success' : 'error'}`}>
                {testResult.success ? (
                  <>
                    ✓ Connection successful!
                  </>
                ) : (
                  <>
                    ✗ {testResult.error}
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn-primary"
            onClick={handleSave}
            disabled={!workerUrl}
          >
            Save Configuration
          </button>
        </div>

        <div className="help-section">
          <h3>Getting Started</h3>
          <ol>
            <li><strong>Deploy the Worker (Required):</strong>
              <pre style={{background: '#1e1e1e', padding: '10px', borderRadius: '4px', marginTop: '8px'}}>
cd ~/cloudflare-dev-agent
wrangler login
wrangler deploy</pre>
            </li>
            <li>Copy the worker URL from the output (e.g., https://cloudflare-dev-agent-worker.YOUR_SUBDOMAIN.workers.dev)</li>
            <li>Paste the worker URL above</li>
            <li>Click "Test Connection"</li>
            <li>If successful, click "Save Configuration"</li>
          </ol>
          <p style={{marginTop: '16px', padding: '12px', background: '#2a2a2a', borderRadius: '4px', fontSize: '14px'}}>
            <strong>Why a worker?</strong> Browsers can't directly call Cloudflare's API due to CORS restrictions.
            The worker acts as a CORS-friendly proxy between your browser and Workers AI.
          </p>
        </div>
      </div>
    </div>
  );
}
